import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  WorkspacePremium as PremiumIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const ACCESS_OPTIONS = [
  { value: 'included', label: 'Included' },
  { value: 'limited', label: 'Limited' },
  { value: 'priority', label: 'Priority' },
  { value: 'locked', label: 'Locked' },
];

const APPS_OPTIONS = [
  { value: 'mitra', label: 'Mitra app' },
  { value: 'react', label: 'React web' },
  { value: 'citizen', label: 'Citizen app' },
  { value: 'both', label: 'Mitra + React' },
];

const emptyPlanForm = () => ({
  name: '',
  purpose: '',
  tagline: '',
  badgeLabel: '',
  monthlyPriceInr: 0,
  yearlyPriceInr: 0,
  isFree: false,
  isHighlighted: false,
  isActive: true,
  sortOrder: 100,
  ctaLabel: 'Choose plan',
});

const emptyFeatureForm = () => ({
  label: '',
  description: '',
  category: 'general',
  apps: 'both',
  sortOrder: 300,
  upgradeTarget: 'prime',
  isActive: true,
});

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const paiseToInr = (paise) => (Number(paise) || 0) / 100;

const PLAN_TABLE_MIN_WIDTH = 1860;
const TEXT_COL_MAX_WIDTH = 300;

const growColWidth = (text, minWidth, maxWidth = TEXT_COL_MAX_WIDTH) => {
  const raw = String(text ?? '').trim();
  if (!raw) return minWidth;
  const estimated = Math.ceil(raw.length * 7.4) + 36;
  return Math.max(minWidth, Math.min(maxWidth, estimated));
};

const growingTextFieldSx = {
  width: '100%',
  maxWidth: '100%',
  '& .MuiInputBase-root': {
    alignItems: 'flex-start',
    py: 0.75,
    width: '100%',
  },
  '& .MuiInputBase-input': {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.4,
  },
};

const tableScrollSx = {
  width: '100%',
  maxWidth: '100%',
  overflowX: 'auto',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(15, 23, 42, 0.35) rgba(15, 23, 42, 0.08)',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  '&::-webkit-scrollbar': { height: 12 },
  '&::-webkit-scrollbar-track': { bgcolor: 'action.hover' },
  '&::-webkit-scrollbar-thumb': {
    bgcolor: 'text.disabled',
    borderRadius: 8,
  },
};

const stickyColSx = (side) => ({
  position: 'sticky',
  [side]: 0,
  zIndex: 3,
  bgcolor: 'background.paper',
  boxShadow:
    side === 'left'
      ? '4px 0 8px rgba(15, 23, 42, 0.08)'
      : '-4px 0 8px rgba(15, 23, 42, 0.08)',
});

const PlanEditFields = ({ plan, onPatch, layout, onDelete }) => {
  const fields = [
    {
      key: 'name',
      label: 'Name',
      wrap: true,
      minWidth: growColWidth(plan.name, 140, 220),
      sticky: 'left',
      node: (
        <Stack direction="row" alignItems="flex-start" spacing={0.25}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              value={plan.name}
              onChange={(e) => onPatch({ name: e.target.value })}
              sx={growingTextFieldSx}
            />
            <Typography variant="caption" display="block" color="text.secondary">
              {plan.planKey}
              {plan.isSystem ? ' · system' : ''}
            </Typography>
          </Box>
          {onDelete && !plan.isSystem ? (
            <IconButton
              size="small"
              color="error"
              aria-label={`Delete ${plan.name || 'plan'}`}
              onClick={() => onDelete(plan)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'purpose',
      label: 'Purpose',
      wrap: true,
      minWidth: growColWidth(plan.purpose, 160),
      node: (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          value={plan.purpose || ''}
          onChange={(e) => onPatch({ purpose: e.target.value })}
          sx={growingTextFieldSx}
        />
      ),
    },
    {
      key: 'tagline',
      label: 'Tagline',
      wrap: true,
      minWidth: growColWidth(plan.tagline, 180),
      node: (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          value={plan.tagline || ''}
          onChange={(e) => onPatch({ tagline: e.target.value })}
          sx={growingTextFieldSx}
        />
      ),
    },
    {
      key: 'badge',
      label: 'Badge',
      wrap: true,
      minWidth: growColWidth(plan.badgeLabel, 110, 200),
      node: (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          value={plan.badgeLabel || ''}
          onChange={(e) => onPatch({ badgeLabel: e.target.value })}
          sx={growingTextFieldSx}
        />
      ),
    },
    {
      key: 'monthly',
      label: 'Monthly ₹',
      minWidth: 100,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          disabled={plan.isFree}
          value={plan._monthlyInr ?? paiseToInr(plan.monthlyPricePaise)}
          onChange={(e) =>
            onPatch({
              _monthlyInr: e.target.value,
              isFree: false,
            })
          }
        />
      ),
    },
    {
      key: 'yearly',
      label: 'Yearly ₹',
      minWidth: 100,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          disabled={plan.isFree}
          value={plan._yearlyInr ?? paiseToInr(plan.yearlyPricePaise)}
          onChange={(e) => onPatch({ _yearlyInr: e.target.value })}
        />
      ),
    },
    {
      key: 'bills',
      label: 'Bills+tokens',
      minWidth: 90,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={plan.billsTokensLimit ?? 0}
          onChange={(e) => onPatch({ billsTokensLimit: e.target.value })}
        />
      ),
    },
    {
      key: 'adsUpdates',
      label: 'Ads updates',
      minWidth: 90,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={plan.adsUpdatesLimit ?? 0}
          onChange={(e) => onPatch({ adsUpdatesLimit: e.target.value })}
        />
      ),
    },
    {
      key: 'adsPamphlets',
      label: 'Ads pamphlets',
      minWidth: 90,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={plan.adsPamphletsLimit ?? 0}
          onChange={(e) => onPatch({ adsPamphletsLimit: e.target.value })}
        />
      ),
    },
    {
      key: 'operators',
      label: 'Operators',
      minWidth: 90,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={plan.operatorsLimit ?? 0}
          onChange={(e) => onPatch({ operatorsLimit: e.target.value })}
        />
      ),
    },
    {
      key: 'radius',
      label: 'Ad radius km',
      minWidth: 100,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={plan.adRadiusKm ?? 5}
          onChange={(e) => onPatch({ adRadiusKm: e.target.value })}
          helperText="Included"
        />
      ),
    },
    {
      key: 'expand',
      label: 'Expand ₹/km/day',
      minWidth: 120,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={
            plan._expandRadiusInr ?? paiseToInr(plan.adRadiusExpandPricePaise)
          }
          onChange={(e) => onPatch({ _expandRadiusInr: e.target.value })}
          helperText="Paid extra km"
        />
      ),
    },
    {
      key: 'maxKm',
      label: 'Max km',
      minWidth: 90,
      node: (
        <TextField
          size="small"
          fullWidth
          type="number"
          value={plan.adRadiusMaxKm ?? 15}
          onChange={(e) => onPatch({ adRadiusMaxKm: e.target.value })}
        />
      ),
    },
    {
      key: 'cta',
      label: 'CTA',
      wrap: true,
      minWidth: growColWidth(plan.ctaLabel, 120, 220),
      node: (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          value={plan.ctaLabel || ''}
          onChange={(e) => onPatch({ ctaLabel: e.target.value })}
          sx={growingTextFieldSx}
        />
      ),
    },
    {
      key: 'flags',
      label: 'Flags',
      minWidth: 130,
      node: (
        <Stack spacing={0}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(plan.isFree)}
                onChange={(e) =>
                  onPatch({
                    isFree: e.target.checked,
                    _monthlyInr: e.target.checked ? 0 : plan._monthlyInr,
                  })
                }
              />
            }
            label="Free"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(plan.isHighlighted)}
                onChange={(e) => onPatch({ isHighlighted: e.target.checked })}
              />
            }
            label="Highlight"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={Boolean(plan.isActive)}
                onChange={(e) => onPatch({ isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </Stack>
      ),
    },
  ];

  if (layout === 'table') {
    return (
      <>
        {fields.map((item) => (
          <TableCell
            key={item.key}
            sx={{
              minWidth: item.minWidth,
              width: item.wrap ? item.minWidth : undefined,
              maxWidth: item.wrap ? TEXT_COL_MAX_WIDTH : undefined,
              verticalAlign: 'top',
              whiteSpace: 'normal',
              ...(item.sticky ? stickyColSx(item.sticky) : {}),
            }}
          >
            {item.node}
          </TableCell>
        ))}
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.5,
      }}
    >
      {fields.map((item) => (
        <Box
          key={item.key}
          sx={{ gridColumn: item.key === 'flags' ? { sm: '1 / -1' } : undefined }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {item.label}
          </Typography>
          {item.node}
        </Box>
      ))}
    </Box>
  );
};

/**
 * AA Login — Membership Plans Management (Phase 1)
  * Dynamic names, prices, feature matrix. Apps tag = Mitra / React / Citizen.
  * Mitra dashboard gating reads membership entitlements from /franchisee/admin/me.
  */
const MembershipPlansAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [schemaAvailable, setSchemaAvailable] = useState(true);
  const [settings, setSettings] = useState({
    enabled: false,
    gstPercent: 18,
    priceIncludesGst: true,
    hsnSacCode: '998314',
    currencyCode: 'INR',
    showLockedFeatures: true,
    upgradeCtaElite: 'Upgrade to Elite',
    upgradeCtaPrime: 'Upgrade to Prime',
    legalBlurb:
      'Loksy Membership is a SaaS platform fee (GST as shown). Cancel stops future renewals; downgrade from next cycle. See https://services.knls.in/refund-policy',
    refundPolicyBlurb:
      'Duplicate Razorpay charges and failed activation after payment may be refunded. Unused prepaid periods may be considered case-by-case and as required by law. Shop UPI payments are with the shop, not Loksy. Full policy: https://services.knls.in/refund-policy',
    trialEnabled: true,
    trialDays: 30,
    trialPlanKey: 'prime',
    alertThresholdPercent: 75,
    billAddonCount: 500,
    billAddonPricePaise: 39900,
    extraAdPricePaise: 7900,
    popupAdPricePaise: 9900,
    consentVersion: 'v1',
    introOfferEnabled: true,
    introPlanKey: 'elite',
    introPricePaise: 19900,
    introMonths: 3,
    offerCardDelayDays: 3,
  });
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [dirtyCells, setDirtyCells] = useState({});
  const [dirtyPlanKeys, setDirtyPlanKeys] = useState({});
  const [savingPlans, setSavingPlans] = useState(false);

  const [planDialog, setPlanDialog] = useState(false);
  const [planForm, setPlanForm] = useState(emptyPlanForm());
  const [planSaving, setPlanSaving] = useState(false);

  const [featureDialog, setFeatureDialog] = useState(false);
  const [featureForm, setFeatureForm] = useState(emptyFeatureForm());
  const [featureSaving, setFeatureSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/membership/admin/catalog`, {
        headers: authHeaders(),
      });
      if (res.data?.success) {
        const data = res.data.data || {};
        setSchemaAvailable(data.schemaAvailable !== false);
        setSettings((prev) => ({ ...prev, ...(data.settings || {}) }));
        setPlans(data.plans || []);
        setFeatures(data.features || []);
        setMatrix(data.matrix || {});
        setDirtyCells({});
        setDirtyPlanKeys({});
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load membership catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Membership Plans - Loksy';
    load();
  }, [load]);

  const activePlanCount = useMemo(
    () => plans.filter((p) => p.isActive).length,
    [plans],
  );

  const cellOf = (featureKey, planKey) => {
    const dirty = dirtyCells[`${featureKey}::${planKey}`];
    if (dirty) return dirty;
    return (
      matrix[featureKey]?.[planKey] || {
        accessLevel: 'locked',
        displayValue: 'Locked',
      }
    );
  };

  const setCell = (featureKey, planKey, patch) => {
    const key = `${featureKey}::${planKey}`;
    const current = cellOf(featureKey, planKey);
    setDirtyCells((prev) => ({
      ...prev,
      [key]: { ...current, ...patch },
    }));
  };

  const dirtyCount = Object.keys(dirtyCells).length;

  const saveSettings = async () => {
    const legal = String(settings.legalBlurb || '').trim();
    const refund = String(settings.refundPolicyBlurb || '').trim();
    const consent = String(settings.consentVersion || '').trim();
    if (!legal || !refund || !consent) {
      toast.error(
        'Legal blurb, refund policy blurb, and consent version are required before saving Membership settings.',
      );
      return;
    }
    setSavingSettings(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/membership/admin/settings`,
        {
          enabled: settings.enabled,
          gstPercent: Number(settings.gstPercent),
          priceIncludesGst: Boolean(settings.priceIncludesGst),
          hsnSacCode: settings.hsnSacCode,
          currencyCode: settings.currencyCode || 'INR',
          showLockedFeatures: Boolean(settings.showLockedFeatures),
          upgradeCtaElite: settings.upgradeCtaElite,
          upgradeCtaPrime: settings.upgradeCtaPrime,
          legalBlurb: settings.legalBlurb,
          refundPolicyBlurb: settings.refundPolicyBlurb,
          trialEnabled: Boolean(settings.trialEnabled),
          trialDays: Number(settings.trialDays ?? 30),
          trialPlanKey: settings.trialPlanKey || 'prime',
          alertThresholdPercent: Number(settings.alertThresholdPercent ?? 75),
          billAddonCount: Number(settings.billAddonCount ?? 500),
          billAddonPricePaise: Number(settings.billAddonPricePaise ?? 39900),
          extraAdPricePaise: Number(settings.extraAdPricePaise ?? 7900),
          popupAdPricePaise: Number(settings.popupAdPricePaise ?? 9900),
          consentVersion: settings.consentVersion || 'v1',
          introOfferEnabled: Boolean(settings.introOfferEnabled !== false),
          introPlanKey: settings.introPlanKey || 'elite',
          introPricePaise: Number(settings.introPricePaise ?? 19900),
          introMonths: Number(settings.introMonths ?? 3),
          offerCardDelayDays: Number(settings.offerCardDelayDays ?? 3),
        },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setSettings((prev) => ({ ...prev, ...res.data.data.settings }));
        toast.success('Membership settings saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const planPayload = (plan) => ({
    name: plan.name,
    purpose: plan.purpose,
    tagline: plan.tagline,
    badgeLabel: plan.badgeLabel,
    monthlyPriceInr: Number(plan._monthlyInr ?? paiseToInr(plan.monthlyPricePaise)),
    yearlyPriceInr: Number(plan._yearlyInr ?? paiseToInr(plan.yearlyPricePaise)),
    isFree: Boolean(plan.isFree),
    isHighlighted: Boolean(plan.isHighlighted),
    isActive: Boolean(plan.isActive),
    sortOrder: Number(plan.sortOrder) || 0,
    ctaLabel: plan.ctaLabel,
    billsTokensLimit: Number(plan.billsTokensLimit ?? 0),
    adsUpdatesLimit: Number(plan.adsUpdatesLimit ?? 0),
    adsPamphletsLimit: Number(plan.adsPamphletsLimit ?? 0),
    operatorsLimit: Number(plan.operatorsLimit ?? 0),
    adRadiusKm: Number(plan.adRadiusKm ?? 5),
    adRadiusExpandPricePaise: Math.round(
      Number(
        plan._expandRadiusInr ??
          paiseToInr(plan.adRadiusExpandPricePaise ?? 9900),
      ) * 100,
    ),
    adRadiusMaxKm: Number(plan.adRadiusMaxKm ?? 15),
  });

  const persistPlan = (plan) =>
    axios.put(
      `${API_BASE_URL}/membership/admin/plans/${plan.planKey}`,
      planPayload(plan),
      { headers: authHeaders() },
    );

  const updatePlanLocal = (planKey, patch) => {
    setPlans((prev) =>
      prev.map((p) => (p.planKey === planKey ? { ...p, ...patch } : p)),
    );
    setDirtyPlanKeys((prev) => ({ ...prev, [planKey]: true }));
  };

  const dirtyPlanCount = Object.keys(dirtyPlanKeys).length;

  const saveAllPlans = async () => {
    const dirty = plans.filter((p) => dirtyPlanKeys[p.planKey]);
    if (!dirty.length) {
      toast.info('No plan changes to save');
      return;
    }
    setSavingPlans(true);
    const saved = [];
    const failed = [];
    for (const plan of dirty) {
      try {
        await persistPlan(plan);
        saved.push(plan.planKey);
      } catch (err) {
        failed.push({
          name: plan.name || plan.planKey,
          message: err.response?.data?.message || 'Could not save',
        });
      }
    }
    setDirtyPlanKeys((prev) => {
      const next = { ...prev };
      saved.forEach((key) => {
        delete next[key];
      });
      return next;
    });
    if (saved.length) {
      toast.success(
        saved.length === 1 ? 'Plan saved' : `${saved.length} plans saved`,
      );
    }
    if (failed.length) {
      toast.error(failed.map((item) => `${item.name}: ${item.message}`).join('; '));
    }
    setSavingPlans(false);
  };

  const saveMatrix = async () => {
    if (!dirtyCount) {
      toast.info('No matrix changes to save');
      return;
    }
    setSavingMatrix(true);
    try {
      const cells = Object.entries(dirtyCells).map(([key, value]) => {
        const [featureKey, planKey] = key.split('::');
        return {
          featureKey,
          planKey,
          accessLevel: value.accessLevel,
          displayValue: value.displayValue,
        };
      });
      const res = await axios.put(
        `${API_BASE_URL}/membership/admin/matrix`,
        { cells },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        const data = res.data.data || {};
        setMatrix(data.matrix || {});
        setDirtyCells({});
        toast.success('Feature matrix saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save matrix');
    } finally {
      setSavingMatrix(false);
    }
  };

  const createPlan = async () => {
    if (!planForm.name.trim()) {
      toast.error('Plan name is required');
      return;
    }
    setPlanSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/membership/admin/plans`,
        {
          ...planForm,
          monthlyPriceInr: Number(planForm.monthlyPriceInr) || 0,
          yearlyPriceInr: Number(planForm.yearlyPriceInr) || 0,
        },
        { headers: authHeaders() },
      );
      toast.success('Plan created');
      setPlanDialog(false);
      setPlanForm(emptyPlanForm());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const createFeature = async () => {
    if (!featureForm.label.trim()) {
      toast.error('Feature label is required');
      return;
    }
    setFeatureSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/membership/admin/features`,
        featureForm,
        { headers: authHeaders() },
      );
      toast.success('Feature added');
      setFeatureDialog(false);
      setFeatureForm(emptyFeatureForm());
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add feature');
    } finally {
      setFeatureSaving(false);
    }
  };

  const deletePlan = async (plan) => {
    if (plan.isSystem) {
      toast.info('System plans cannot be deleted — turn off Active instead');
      return;
    }
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/membership/admin/plans/${plan.planKey}`, {
        headers: authHeaders(),
      });
      toast.success('Plan deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete plan');
    }
  };

  const deleteFeature = async (feature) => {
    if (feature.isSystem) {
      toast.info('System features cannot be deleted — turn off Active instead');
      return;
    }
    if (!window.confirm(`Delete feature "${feature.label}"?`)) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/membership/admin/features/${feature.featureKey}`,
        { headers: authHeaders() },
      );
      toast.success('Feature deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete feature');
    }
  };

  const toggleFeatureActive = async (feature, next) => {
    try {
      await axios.put(
        `${API_BASE_URL}/membership/admin/features/${feature.featureKey}`,
        { isActive: next },
        { headers: authHeaders() },
      );
      setFeatures((prev) =>
        prev.map((f) =>
          f.featureKey === feature.featureKey ? { ...f, isActive: next } : f,
        ),
      );
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update feature');
    }
  };

  return (
    <DashboardLayout>
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, md: 3 },
          px: { xs: 1.5, sm: 2, md: 3 },
          minWidth: 0,
          width: '100%',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          mb={2}
        >
          <Box>
            <Button
              component={RouterLink}
              to="/admin-control-screens"
              startIcon={<ArrowBackIcon />}
              size="small"
              sx={{ mb: 0.5 }}
            >
              Admin Control
            </Button>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <PremiumIcon color="warning" />
              <Typography variant="h5" fontWeight={800}>
                Membership Plans
              </Typography>
              <Button
                component={RouterLink}
                to="/content/membership-subscribers"
                size="small"
              >
                Subscriber counts
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Connect · Basic · Advance · Prime — volume subscription. Trial, caps and add-on prices are live for Mitra after Save.
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>

        {!schemaAvailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Membership tables are not migrated yet. Restart tsbackend so migration{' '}
            <strong>141_locsy_membership_plans.sql</strong> can apply, then refresh.
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Apps column</strong> on each feature: <Chip size="small" label="Mitra app" sx={{ mx: 0.5 }} />{' '}
          = Flutter Mitra Operate/Grow · <Chip size="small" label="React web" sx={{ mx: 0.5 }} /> = franchisee
          panel on services.knls.in · <Chip size="small" label="Citizen app" sx={{ mx: 0.5 }} /> = public shop
          page. Free is listing-only: lock <em>Mitra dashboard</em> to hide bills/stock/ads. Paid plans differ by
          monthly volume. Trial days = 0 turns trial off. Hide coins — do not sell coin packs.
        </Alert>

        {loading ? (
          <Box py={6} textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ minWidth: 0, width: '100%' }}>
            {/* Settings */}
            <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, minWidth: 0, overflow: 'hidden' }}>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Product &amp; compliance
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(settings.enabled)}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, enabled: e.target.checked }))
                      }
                    />
                  }
                  label={
                    settings.enabled
                      ? `Membership product ON (public catalog live) · ${activePlanCount} active plans`
                      : 'Membership product OFF (admin can edit; apps ignore until ON)'
                  }
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="GST %"
                    type="number"
                    size="small"
                    value={settings.gstPercent}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        gstPercent: e.target.value,
                      }))
                    }
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="HSN / SAC"
                    size="small"
                    value={settings.hsnSacCode}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        hsnSacCode: e.target.value,
                      }))
                    }
                    sx={{ width: 140 }}
                    helperText="998314 typical for IT/SaaS"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(settings.priceIncludesGst)}
                        onChange={(e) =>
                          setSettings((s) => ({
                            ...s,
                            priceIncludesGst: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Prices include GST"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(settings.showLockedFeatures)}
                        onChange={(e) =>
                          setSettings((s) => ({
                            ...s,
                            showLockedFeatures: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Show locked features (soft lock)"
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Basic upgrade CTA"
                    value={settings.upgradeCtaElite}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        upgradeCtaElite: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Prime upgrade CTA"
                    value={settings.upgradeCtaPrime}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        upgradeCtaPrime: e.target.value,
                      }))
                    }
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(settings.trialEnabled)}
                        onChange={(e) =>
                          setSettings((s) => ({ ...s, trialEnabled: e.target.checked }))
                        }
                      />
                    }
                    label="Free signup demo enabled"
                  />
                  <TextField
                    select
                    label="Free demo plan"
                    size="small"
                    value={settings.trialPlanKey || 'prime'}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, trialPlanKey: e.target.value }))
                    }
                    helperText="New registrations only; no payment for the demo."
                    sx={{ minWidth: 230 }}
                  >
                    <MenuItem value="elite">Basic</MenuItem>
                    <MenuItem value="advance">Advance</MenuItem>
                    <MenuItem value="prime">Prime</MenuItem>
                  </TextField>
                  <TextField
                    label="Trial days (0 = off)"
                    type="number"
                    size="small"
                    value={settings.trialDays ?? 30}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, trialDays: e.target.value }))
                    }
                    sx={{ width: 160 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(settings.introOfferEnabled !== false)}
                        onChange={(e) =>
                          setSettings((s) => ({
                            ...s,
                            introOfferEnabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Basic intro offer on"
                  />
                  <TextField
                    label="Intro offer ₹"
                    type="number"
                    size="small"
                    value={paiseToInr(settings.introPricePaise ?? 19900)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        introPricePaise: Math.round(Number(e.target.value || 0) * 100),
                      }))
                    }
                    sx={{ width: 140 }}
                    helperText="First paid Basic"
                  />
                  <TextField
                    label="Intro months"
                    type="number"
                    size="small"
                    value={settings.introMonths ?? 3}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, introMonths: e.target.value }))
                    }
                    sx={{ width: 140 }}
                    helperText="Then list price"
                  />
                  <TextField
                    label="Offer card after days"
                    type="number"
                    size="small"
                    value={settings.offerCardDelayDays ?? 3}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        offerCardDelayDays: e.target.value,
                      }))
                    }
                    sx={{ width: 180 }}
                    helperText="Days on Free after trial"
                  />
                  <TextField
                    label="Alert %"
                    type="number"
                    size="small"
                    value={settings.alertThresholdPercent ?? 75}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        alertThresholdPercent: e.target.value,
                      }))
                    }
                    sx={{ width: 120 }}
                    helperText="Warn at this % of cap"
                  />
                  <TextField
                    label="Bill add-on count"
                    type="number"
                    size="small"
                    value={settings.billAddonCount ?? 500}
                    onChange={(e) =>
                      setSettings((s) => ({ ...s, billAddonCount: e.target.value }))
                    }
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="Bill add-on ₹"
                    type="number"
                    size="small"
                    value={paiseToInr(settings.billAddonPricePaise)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        billAddonPricePaise: Math.round(Number(e.target.value || 0) * 100),
                      }))
                    }
                    sx={{ width: 140 }}
                  />
                  <TextField
                    label="Extra ad ₹"
                    type="number"
                    size="small"
                    value={paiseToInr(settings.extraAdPricePaise)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        extraAdPricePaise: Math.round(Number(e.target.value || 0) * 100),
                      }))
                    }
                    sx={{ width: 130 }}
                  />
                  <TextField
                    label="Popup ad ₹"
                    type="number"
                    size="small"
                    value={paiseToInr(settings.popupAdPricePaise)}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        popupAdPricePaise: Math.round(Number(e.target.value || 0) * 100),
                      }))
                    }
                    sx={{ width: 130 }}
                  />
                </Stack>
                <Alert severity="info">
                  List price for Basic stays as set on the plan row (₹499). Intro
                  is only the first paid Basic months. After trial the shop is
                  Free immediately; Subscription already shows the intro price.
                  The louder home card appears after the delay days if they have
                  not paid. Advance and Prime stay at full price.
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  required
                  label="Legal blurb (T&C / DPDP note) — required"
                  helperText="Shown on Mitra membership checkout. Cannot be empty."
                  value={settings.legalBlurb}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, legalBlurb: e.target.value }))
                  }
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  required
                  label="Refund policy blurb — required"
                  helperText="Shown on Mitra membership pay dialog. Link full policy at /refund-policy."
                  value={settings.refundPolicyBlurb}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      refundPolicyBlurb: e.target.value,
                    }))
                  }
                />
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={saveSettings}
                    disabled={savingSettings}
                  >
                    {savingSettings ? 'Saving…' : 'Save settings'}
                  </Button>
                </Box>
              </Stack>
            </Paper>

            {/* Plans */}
            <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, minWidth: 0, overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1}
                mb={1.5}
              >
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="h6" fontWeight={700}>
                    Plans (names &amp; prices)
                  </Typography>
                  {dirtyPlanCount > 0 ? (
                    <Chip
                      size="small"
                      color="warning"
                      label={`${dirtyPlanCount} unsaved`}
                    />
                  ) : null}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => {
                      setPlanForm(emptyPlanForm());
                      setPlanDialog(true);
                    }}
                  >
                    Add plan
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!dirtyPlanCount || savingPlans}
                    onClick={saveAllPlans}
                  >
                    {savingPlans ? 'Saving…' : 'Save plans'}
                  </Button>
                </Stack>
              </Stack>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Ad radius is included in every plan. Mitra can publish ads only
                inside that km from the shop pin. Expanding beyond it is paid
                at the plan&apos;s Expand ₹/km/day rate through the existing ad
                payment gateway.
              </Alert>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: { xs: 'none', md: 'block' }, mb: 1 }}
              >
                Scroll sideways to reach every column. After you edit, use Save plans.
              </Typography>

              <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                {plans.map((plan) => (
                  <Paper key={plan.planKey} variant="outlined" sx={{ p: 1.5 }}>
                    <PlanEditFields
                      plan={plan}
                      onPatch={(patch) => updatePlanLocal(plan.planKey, patch)}
                      onDelete={deletePlan}
                      layout="cards"
                    />
                  </Paper>
                ))}
              </Stack>

              <TableContainer sx={{ ...tableScrollSx, display: { xs: 'none', md: 'block' } }}>
                <Table size="small" sx={{ minWidth: PLAN_TABLE_MIN_WIDTH }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 140, fontWeight: 700, whiteSpace: 'normal', ...stickyColSx('left') }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ minWidth: 160, fontWeight: 700, whiteSpace: 'normal' }}>Purpose</TableCell>
                      <TableCell sx={{ minWidth: 180, fontWeight: 700, whiteSpace: 'normal' }}>Tagline</TableCell>
                      <TableCell sx={{ minWidth: 110, fontWeight: 700, whiteSpace: 'normal' }}>Badge</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Monthly ₹</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Yearly ₹</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Bills+tokens</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Ads updates</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Ads pamphlets</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Operators</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Ad radius km</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Expand ₹/km/day</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Max km</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>CTA</TableCell>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Flags</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plans.map((plan) => (
                      <TableRow key={plan.planKey} hover>
                        <PlanEditFields
                          plan={plan}
                          onPatch={(patch) => updatePlanLocal(plan.planKey, patch)}
                          onDelete={deletePlan}
                          layout="table"
                        />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box mt={2}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={!dirtyPlanCount || savingPlans}
                  onClick={saveAllPlans}
                >
                  {savingPlans ? 'Saving…' : 'Save plans'}
                </Button>
              </Box>
            </Paper>

            {/* Feature matrix */}
            <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, minWidth: 0, overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1}
                mb={1.5}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Feature matrix
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Access: Included / Limited / Priority / Locked · Display text shows
                    on comparison table
                    {dirtyCount > 0 ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={`${dirtyCount} unsaved`}
                        sx={{ ml: 1 }}
                      />
                    ) : null}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => {
                      setFeatureForm(emptyFeatureForm());
                      setFeatureDialog(true);
                    }}
                  >
                    Add feature
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={!dirtyCount || savingMatrix}
                    onClick={saveMatrix}
                  >
                    {savingMatrix ? 'Saving…' : 'Save matrix'}
                  </Button>
                </Stack>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1 }}
              >
                Scroll sideways to compare every plan column.
              </Typography>
              <TableContainer sx={tableScrollSx}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{ minWidth: 220 + Math.max(plans.length, 1) * 200 }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 180, fontWeight: 700 }}>
                        Feature
                      </TableCell>
                      {plans.map((p) => (
                        <TableCell
                          key={p.planKey}
                          sx={{
                            minWidth: 200,
                            fontWeight: 700,
                            bgcolor: p.isHighlighted ? 'warning.light' : 'grey.100',
                          }}
                        >
                          {p.name}
                          {!p.isActive && (
                            <Chip size="small" label="off" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right"> </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {features.map((feature) => (
                      <TableRow
                        key={feature.featureKey}
                        sx={{ opacity: feature.isActive ? 1 : 0.55 }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {feature.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {feature.featureKey} · {feature.category}
                          </Typography>
                          <Stack direction="row" spacing={0.75} mt={0.75} alignItems="center" flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Apps</InputLabel>
                              <Select
                                label="Apps"
                                value={feature.apps || 'both'}
                                onChange={(e) => {
                                  const apps = e.target.value;
                                  axios
                                    .patch(
                                      `${API_BASE_URL}/membership/admin/features/${feature.featureKey}`,
                                      { apps },
                                      { headers: authHeaders() },
                                    )
                                    .then(() => {
                                      setFeatures((list) =>
                                        list.map((f) =>
                                          f.featureKey === feature.featureKey
                                            ? { ...f, apps }
                                            : f,
                                        ),
                                      );
                                      toast.success(`Apps → ${apps}`);
                                    })
                                    .catch((err) =>
                                      toast.error(
                                        err.response?.data?.message ||
                                          'Could not update apps',
                                      ),
                                    );
                                }}
                              >
                                {APPS_OPTIONS.map((o) => (
                                  <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                feature.apps === 'mitra'
                                  ? 'success'
                                  : feature.apps === 'react'
                                    ? 'primary'
                                    : feature.apps === 'citizen'
                                      ? 'warning'
                                      : 'default'
                              }
                              label={
                                APPS_OPTIONS.find((o) => o.value === (feature.apps || 'both'))
                                  ?.label || 'Mitra + React'
                              }
                            />
                          </Stack>
                          <Box>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={Boolean(feature.isActive)}
                                  onChange={(e) =>
                                    toggleFeatureActive(feature, e.target.checked)
                                  }
                                />
                              }
                              label="Active"
                            />
                          </Box>
                        </TableCell>
                        {plans.map((plan) => {
                          const cell = cellOf(feature.featureKey, plan.planKey);
                          return (
                            <TableCell key={`${feature.featureKey}-${plan.planKey}`}>
                              <Stack spacing={0.75}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Access</InputLabel>
                                  <Select
                                    label="Access"
                                    value={cell.accessLevel || 'locked'}
                                    onChange={(e) =>
                                      setCell(feature.featureKey, plan.planKey, {
                                        accessLevel: e.target.value,
                                        displayValue:
                                          e.target.value === 'locked'
                                            ? cell.displayValue || 'Locked'
                                            : cell.displayValue,
                                      })
                                    }
                                  >
                                    {ACCESS_OPTIONS.map((o) => (
                                      <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <TextField
                                  size="small"
                                  fullWidth
                                  multiline
                                  minRows={1}
                                  maxRows={6}
                                  label="Display"
                                  value={cell.displayValue || ''}
                                  onChange={(e) =>
                                    setCell(feature.featureKey, plan.planKey, {
                                      displayValue: e.target.value,
                                    })
                                  }
                                  sx={growingTextFieldSx}
                                />
                              </Stack>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          {!feature.isSystem && (
                            <Tooltip title="Delete custom feature">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteFeature(feature)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        )}

        {/* Create plan dialog */}
        <Dialog
          open={planDialog}
          onClose={() => !planSaving && setPlanDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Add membership plan</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Name"
                required
                value={planForm.name}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, name: e.target.value }))
                }
              />
              <TextField
                label="Purpose"
                value={planForm.purpose}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, purpose: e.target.value }))
                }
              />
              <TextField
                label="Tagline"
                value={planForm.tagline}
                onChange={(e) =>
                  setPlanForm((f) => ({ ...f, tagline: e.target.value }))
                }
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Monthly ₹"
                  type="number"
                  fullWidth
                  value={planForm.monthlyPriceInr}
                  onChange={(e) =>
                    setPlanForm((f) => ({
                      ...f,
                      monthlyPriceInr: e.target.value,
                      isFree: Number(e.target.value) === 0,
                    }))
                  }
                />
                <TextField
                  label="Yearly ₹"
                  type="number"
                  fullWidth
                  value={planForm.yearlyPriceInr}
                  onChange={(e) =>
                    setPlanForm((f) => ({
                      ...f,
                      yearlyPriceInr: e.target.value,
                    }))
                  }
                />
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={planForm.isHighlighted}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        isHighlighted: e.target.checked,
                      }))
                    }
                  />
                }
                label="Highlight as recommended"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlanDialog(false)} disabled={planSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={createPlan} disabled={planSaving}>
              {planSaving ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create feature dialog */}
        <Dialog
          open={featureDialog}
          onClose={() => !featureSaving && setFeatureDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Add feature</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <TextField
                label="Label"
                required
                value={featureForm.label}
                onChange={(e) =>
                  setFeatureForm((f) => ({ ...f, label: e.target.value }))
                }
              />
              <TextField
                label="Description"
                value={featureForm.description}
                onChange={(e) =>
                  setFeatureForm((f) => ({
                    ...f,
                    description: e.target.value,
                  }))
                }
              />
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={featureForm.category}
                  onChange={(e) =>
                    setFeatureForm((f) => ({
                      ...f,
                      category: e.target.value,
                    }))
                  }
                >
                  {[
                    'listing',
                    'communication',
                    'growth',
                    'tools',
                    'analytics',
                    'general',
                  ].map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Apps</InputLabel>
                <Select
                  label="Apps"
                  value={featureForm.apps || 'both'}
                  onChange={(e) =>
                    setFeatureForm((f) => ({
                      ...f,
                      apps: e.target.value,
                    }))
                  }
                >
                  {APPS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Upgrade target</InputLabel>
                <Select
                  label="Upgrade target"
                  value={featureForm.upgradeTarget || ''}
                  onChange={(e) =>
                    setFeatureForm((f) => ({
                      ...f,
                      upgradeTarget: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="elite">Elite</MenuItem>
                  <MenuItem value="prime">Prime</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setFeatureDialog(false)}
              disabled={featureSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={createFeature}
              disabled={featureSaving}
            >
              {featureSaving ? 'Adding…' : 'Add feature'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default MembershipPlansAdmin;
